// Task #3653 — Contratto dati unico BE↔FE: schemi Zod di RISPOSTA per la
// dashboard admin tenant (route backend `dashboard-unified.routes.ts` +
// `/analytics/heatmap`). Il tipo TS è derivato con `z.infer` ed è la SoT
// condivisa: il backend tipizza il payload di `res.json`, il FE lo usa come
// generic di `apiClient.get<T>`. Vedere docs/zod-boundary.md § Risposte.
import { z } from 'zod';

export const unifiedDashboardDataSchema = z.object({
  voip: z.object({
    active_calls: z.number(),
    today: z.object({
      total_calls: z.number(),
      answered: z.number(),
      missed: z.number(),
      // Il backend normalizza con parseFloat prima di rispondere.
      avg_duration: z.number(),
      total_duration: z.number(),
      inbound: z.number(),
      outbound: z.number(),
    }),
    // Il backend normalizza i COUNT a number (parseInt) prima di rispondere.
    extensions: z.object({ total: z.number(), registered: z.number() }),
    trunks: z.array(z.object({
      id: z.string(),
      name: z.string(),
      provider: z.string(),
      status: z.string(),
    })),
    top_extensions: z.array(z.object({
      extension: z.string(),
      display_name: z.string(),
      total_calls: z.number(),
      total_duration: z.number(),
    })),
    recent_calls: z.array(z.object({
      id: z.string(),
      caller_id_number: z.string(),
      destination_number: z.string(),
      call_direction: z.string(),
      hangup_disposition: z.string(),
      duration: z.number(),
      start_time: z.string(),
      mos_score: z.number().optional(),
    })),
  }),
  crm: z.object({
    contacts: z.number(),
    companies: z.number(),
    leads: z.number(),
    active_campaigns: z.number(),
    leads_by_stage: z.array(z.object({
      id: z.string(),
      name: z.string(),
      color: z.string(),
      position: z.number(),
      count: z.number(),
    })),
    recent_leads: z.array(z.object({
      id: z.string(),
      title: z.string(),
      source: z.string(),
      created_at: z.string(),
      contact_name: z.string(),
      contact_email: z.string(),
      contact_phone: z.string().optional(),
      stage_name: z.string(),
      stage_color: z.string(),
    })),
    leads_daily_7d: z.array(z.number()),
  }),
  tasks: z.object({
    total: z.number(),
    open: z.number(),
    completed: z.number(),
    overdue: z.number(),
    completed_today: z.number(),
    created_today: z.number(),
    created_yesterday: z.number(),
    due_today: z.number(),
    by_status: z.array(z.object({ name: z.string(), color: z.string(), count: z.number() })),
    daily_7d: z.array(z.object({ created: z.number(), completed: z.number() })),
    urgent_list: z.array(z.object({
      id: z.string(),
      title: z.string(),
      priority: z.string(),
      due_date: z.string().nullable(),
      status_name: z.string(),
      status_color: z.string(),
      assignee_name: z.string(),
    })),
  }),
  ai: z.object({
    agents: z.array(z.object({
      id: z.string(),
      name: z.string(),
      status: z.string(),
      elevenlabs_agent_id: z.string(),
    })),
    agents_count: z.number(),
    active_agents: z.number(),
    conversations_today: z.number(),
    avg_duration_today: z.number(),
    conversations_total: z.number(),
    completed_total: z.number(),
    failed_total: z.number(),
    daily_7d: z.array(z.number()),
    recent_conversations: z.array(z.object({
      id: z.string(),
      agent_name: z.string(),
      contact_info: z.string(),
      status: z.string(),
      duration_seconds: z.number(),
      created_at: z.string(),
      // La UNION SQL del backend emette anche 'gemini-voice' (gemini_voice_sessions).
      source: z.enum(['elevenlabs', 'chatbot', 'assistant', 'gemini-voice']),
    })),
  }),
  messaging: z.object({
    total_24h: z.number(),
    sent_24h: z.number(),
    received_24h: z.number(),
    by_channel: z.record(z.object({ total: z.number(), sent: z.number(), received: z.number() })),
  }),
  storage: z.object({
    used_bytes: z.number(),
    max_bytes: z.number(),
    recordings_bytes: z.number(),
    logs_bytes: z.number(),
    documents_bytes: z.number(),
    videos_bytes: z.number(),
    usage_percent: z.number(),
  }).nullable(),
  calendar: z.object({
    today_appointments: z.array(z.object({
      id: z.string(),
      title: z.string(),
      start_time: z.string(),
      end_time: z.string(),
      status: z.string(),
      type_name: z.string(),
      type_color: z.string(),
      customer_name: z.string(),
    })),
  }),
  video: z.object({ active_rooms: z.number() }),
  timestamp: z.string(),
});
export type UnifiedDashboardData = z.infer<typeof unifiedDashboardDataSchema>;

export const dashboardActivityFeedItemSchema = z.object({
  id: z.string(),
  type: z.string(),
  title: z.string(),
  description: z.string(),
  timestamp: z.string(),
  icon: z.string(),
  metadata: z.record(z.unknown()),
});
export type DashboardActivityFeedItem = z.infer<typeof dashboardActivityFeedItemSchema>;

export const dashboardCommStatsSchema = z.object({
  period: z.string(),
  bucket_format: z.string(),
  series: z.array(z.object({
    time: z.string(),
    voip: z.number(),
    messaging: z.number(),
    ai: z.number(),
    video: z.number(),
    total: z.number(),
  })),
  totals: z.object({
    voip: z.number(),
    messaging: z.number(),
    ai: z.number(),
    video: z.number(),
    total: z.number(),
  }),
});
export type DashboardCommStats = z.infer<typeof dashboardCommStatsSchema>;

// Metriche qualità: valori medi formattati lato SQL/BE come stringhe.
export const dashboardCallQualitySchema = z.object({
  period: z.string(),
  total_calls: z.number(),
  answered_calls: z.number(),
  asr: z.string(),
  acd: z.string(),
  avg_pdd: z.string(),
  pdd_95th: z.string(),
  avg_jitter: z.string(),
  avg_packet_loss: z.string(),
  avg_mos: z.string(),
  quality_rating: z.string(),
});
export type DashboardCallQuality = z.infer<typeof dashboardCallQualitySchema>;

export const dashboardHeatmapCellSchema = z.object({
  day: z.number(),
  hour: z.number(),
  count: z.number(),
});
export type DashboardHeatmapCell = z.infer<typeof dashboardHeatmapCellSchema>;
